import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Fade,
  Zoom,
  Stack,
  useTheme,
  alpha
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Google,
  Email,
  Lock,
  Login as LoginIcon,
  TrendingUp
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { useNavigate, useLocation } from 'react-router-dom';
import authService from '../../services/authService';

// Animations
const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-3px);
  }
`;

const gradientShift = keyframes`
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
`;

const pulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(191, 155, 48, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(191, 155, 48, 0);
  }
`;

const LoginForm = ({ onClose, onSwitchToRegister }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email.trim() || !password.trim()) {
      setError('Please enter both email and password');
      return;
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await authService.login(email, password);
      
      // Close modal if it exists
      if (onClose) onClose();
      
      // Get the intended destination from location state or default to home
      const from = location.state?.from?.pathname || '/';
      
      if (from === '/login') {
        // If they came from login page directly, go to home
        navigate('/');
      } else {
        // Go to their intended destination or home
        navigate(from, { replace: true });
      }
      
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.error || 'Invalid email or password. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = () => {
    if (onClose) onClose();
    navigate('/forgot-password');
  };

  const handleGoogleLogin = () => {
    // Implement Google OAuth login
    console.log('Google login clicked');
  };

  const handleSwitchToRegister = () => {
    if (onSwitchToRegister) {
      onSwitchToRegister();
    } else if (onClose) {
      onClose();
      // Navigate to home with register modal
      navigate('/?auth=register');
    } else {
      navigate('/?auth=register');
    }
  };

  return (
    <Fade in timeout={600}>
      <Card 
        sx={{ 
          width: '100%',
          maxWidth: { xs: '100%', sm: 480 },
          mx: 'auto', 
          borderRadius: { xs: '2px', sm: '2px' },
          overflow: 'hidden',
          position: 'relative',
          background: isDarkMode
            ? 'linear-gradient(145deg, rgba(18,18,18,0.95) 0%, rgba(30,30,30,0.95) 100%)'
            : 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(250,250,250,0.95) 100%)',
          backdropFilter: 'none',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          boxShadow: `
            0 20px 40px ${alpha(theme.palette.common.black, 0.1)},
            0 0 0 1px ${alpha(theme.palette.primary.main, 0.05)},
            inset 0 1px 0 ${alpha(theme.palette.common.white, 0.1)}
          `,
          animation: { xs: 'none', sm: `${float} 6s ease-in-out infinite` },
        }}
      >
        {/* Gradient Background */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            background: 'linear-gradient(135deg, #5980a6 0%, #7ba0c4 50%, #5980a6 100%)',
            backgroundSize: '200% 200%',
            animation: `${gradientShift} 8s ease infinite`,
            opacity: 0.05,
            zIndex: 0
          }}
        />
        
        <CardContent sx={{ p: { xs: 3, sm: 5 }, position: 'relative', zIndex: 1 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
            <Zoom in timeout={800}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 64, sm: 80 },
                  height: { xs: 64, sm: 80 },
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #5980a6 0%, #7ba0c4 100%)',
                  mb: { xs: 2, sm: 3 },
                  animation: `${pulse} 2s infinite`,
                }}
              >
                <TrendingUp sx={{ fontSize: { xs: 32, sm: 40 }, color: 'white' }} />
              </Box>
            </Zoom>
            
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 800,
                fontSize: { xs: '1.5rem', sm: '2rem' },
                background: 'linear-gradient(135deg, #5980a6 0%, #7ba0c4 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              Welcome Back
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                fontWeight: 500,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Sign in to access your trading signals
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Fade in>
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3, 
                  borderRadius: '2px',
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
                }}
              >
                {error}
              </Alert>
            </Fade>
          )}

          {/* Login Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
            <Stack spacing={3}>
              <TextField
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField('')}
                fullWidth
                disabled={isSubmitting}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email 
                        sx={{ 
                          color: focusedField === 'email' ? 'primary.main' : 'text.secondary',
                          transition: 'color 0.2s ease'
                        }} 
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '2px',
                    backgroundColor: alpha(theme.palette.background.paper, 0.8),
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.background.paper, 0.9),
                    },
                    '&.Mui-focused': {
                      backgroundColor: alpha(theme.palette.background.paper, 1),
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
                    }
                  }
                }}
              />

              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField('')}
                fullWidth
                disabled={isSubmitting}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock 
                        sx={{ 
                          color: focusedField === 'password' ? 'primary.main' : 'text.secondary',
                          transition: 'color 0.2s ease'
                        }} 
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={isSubmitting}
                        edge="end"
                        sx={{ 
                          color: 'text.secondary',
                          '&:hover': { 
                            color: 'primary.main',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1)
                          }
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '2px',
                    backgroundColor: alpha(theme.palette.background.paper, 0.8),
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.background.paper, 0.9),
                    },
                    '&.Mui-focused': {
                      backgroundColor: alpha(theme.palette.background.paper, 1),
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
                    }
                  }
                }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                sx={{
                  borderRadius: '2px',
                  py: { xs: 1.5, sm: 1.8 },
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #5980a6 0%, #7ba0c4 100%)',
                  boxShadow: `0 8px 25px ${alpha('#5980a6', 0.4)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7ba0c4 0%, #5980a6 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 35px ${alpha('#5980a6', 0.5)}`,
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  }
                }}
              >
                {isSubmitting ? 'Signing In...' : 'Sign In'}
              </Button>
            </Stack>
          </Box>

          {/* Forgot Password */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Link
              component="button"
              type="button"
              onClick={handleForgotPassword}
              disabled={isSubmitting}
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'primary.dark'
                }
              }}
            >
              Forgot your password?
            </Link>
          </Box>

          {/* Divider */}
          <Divider sx={{ my: 3, opacity: 0.7 }}>
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, fontWeight: 500 }}>
              or continue with
            </Typography>
          </Divider>

          {/* Google Login */}
          <Button
            variant="outlined"
            fullWidth
            size="large"
            disabled={isSubmitting}
            startIcon={<Google />}
            onClick={handleGoogleLogin}
            sx={{
              borderRadius: '2px',
              py: { xs: 1.2, sm: 1.5 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 600,
              borderColor: alpha(theme.palette.divider, 0.8),
              color: 'text.primary',
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.1)}`,
              }
            }}
          >
            Continue with Google
          </Button>

          {/* Register Link */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Don't have an account?{' '}
              <Link
                component="button"
                type="button"
                onClick={handleSwitchToRegister}
                disabled={isSubmitting}
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontWeight: 700,
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'primary.dark'
                  }
                }}
              >
                Sign up for free
              </Link>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );
};

export default LoginForm;